import { __extends } from "tslib";
import { Vector3D } from '@awayjs/core';
import { TaskBase } from './TaskBase';
import { ContextGLProgramType } from '../../base/ContextGLProgramType';
var ThresholdTask = /** @class */ (function (_super) {
    __extends(ThresholdTask, _super);
    function ThresholdTask() {
        var _this = _super.call(this) || this;
        _this._opDecode = {
            '<': 'slt ',
            '<=': 'sge ',
            '>': 'slt ',
            '>=': 'sge ',
            '==': 'seq ',
            '!=': 'sne '
        };
        _this._thDecode = {
            '<': false,
            '<=': true,
            '>': true,
            '>=': false,
            '==': false,
            '!=': false
        };
        _this._operation = '<';
        _this._op = 'slt ';
        _this._th = false;
        _this._threshold = 0;
        _this._thresholdRGBA = new Vector3D(0, 0, 0, 0);
        _this._color = 0;
        _this._colorRGBA = new Vector3D(0, 0, 0, 0);
        _this._mask = 0;
        _this._maskRGBA = new Vector3D(1, 1, 1, 1);
        _this._copySource = false;
        _this._decodeVector = new Vector3D(65025.0, 255.0, 1.0, 16581375.0);
        _this._fragmentConstantData = new Float32Array([
            0.0, 0.0, 0.0, 0.0,
            0.0, 0.0, 0.0, 0.0,
            0.0, 0.0, 0.0, 0.0,
            65025.0, 255.0, 1.0, 16581375.0 //decode
        ]);
        return _this;
    }
    Object.defineProperty(ThresholdTask.prototype, "operation", {
        get: function () {
            return this._operation;
        },
        set: function (value) {
            if (this._operation == value)
                return;
            this._operation = value;
            this._op = this._opDecode[value];
            this._th = this._thDecode[value];
            this.invalidateProgram();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ThresholdTask.prototype, "threshold", {
        get: function () {
            return this._threshold;
        },
        set: function (value) {
            if (this._threshold == value)
                return;
            this._threshold = value;
            this._thresholdRGBA.x = ((value >> 16) & 0xff) / 0xff;
            this._thresholdRGBA.y = ((value >> 8) & 0xff) / 0xff;
            this._thresholdRGBA.z = (value & 0xff) / 0xff;
            this._thresholdRGBA.w = ((value >> 24) & 0xff) / 0xff;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ThresholdTask.prototype, "color", {
        get: function () {
            return this._color;
        },
        set: function (value) {
            if (this._color == value)
                return;
            this._color = value;
            //pre-multiply alpha
            var alpha = ((value >> 24) & 0xff) / 0xff;
            this._colorRGBA.x = alpha * ((value >> 16) & 0xff) / 0xff;
            this._colorRGBA.y = alpha * ((value >> 8) & 0xff) / 0xff;
            this._colorRGBA.z = alpha * (value & 0xff) / 0xff;
            this._colorRGBA.w = alpha;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ThresholdTask.prototype, "mask", {
        get: function () {
            return this._mask;
        },
        set: function (value) {
            if (this._mask == value)
                return;
            this._mask = value;
            this._maskRGBA.x = ((value >> 16) & 0xff) / 0xff;
            this._maskRGBA.y = ((value >> 8) & 0xff) / 0xff;
            this._maskRGBA.z = (value & 0xff) / 0xff;
            this._maskRGBA.w = ((value >> 24) & 0xff) / 0xff;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ThresholdTask.prototype, "copySource", {
        get: function () {
            return this._copySource;
        },
        set: function (value) {
            if (this._copySource == value)
                return;
            this._copySource = value;
            this.invalidateProgram();
        },
        enumerable: false,
        configurable: true
    });
    ThresholdTask.prototype.getFragmentCode = function () {
        var temp1 = this._registerCache.getFreeFragmentVectorTemp();
        this._registerCache.addFragmentTempUsages(temp1, 1);
        var inputTexture = this._registerCache.getFreeTextureReg();
        this.sourceSamplerIndex = inputTexture.index;
        var threshold = this._registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = threshold.index;
        var color = this._registerCache.getFreeFragmentConstant();
        var mask = this._registerCache.getFreeFragmentConstant();
        var decode = this._registerCache.getFreeFragmentConstant();
        var code;
        if (this._copySource) {
            var temp2 = this._registerCache.getFreeFragmentVectorTemp();
            this._registerCache.addFragmentTempUsages(temp2, 1);
            var temp3 = this._registerCache.getFreeFragmentVectorTemp();
            this._registerCache.addFragmentTempUsages(temp3, 1);
            code = 'tex ' + temp1 + ', ' + this._uvVarying + ', ' + inputTexture + ' <2d,linear,clamp>\n' +
                'mul ' + temp2 + ', ' + temp1 + ', ' + mask + '\n' +
                'dp4 ' + temp2 + ', ' + temp2 + ', ' + decode + '\n' +
                // eslint-disable-next-line max-len
                this._op + temp2 + ', ' + (this._th ? threshold : temp2) + ', ' + (this._th ? temp2 : threshold) + '\n' +
                'sub ' + temp3 + ', ' + decode + '.zzzz, ' + temp2 + '\n' +
                'mul ' + temp2 + ', ' + temp2 + ', ' + color + '\n' +
                'mul ' + temp3 + ', ' + temp3 + ', ' + temp1 + '\n' +
                'add ' + temp1 + ', ' + temp2 + ', ' + temp3 + '\n' +
                'mov oc, ' + temp1 + '\n';
            this._registerCache.removeFragmentTempUsage(temp3);
            this._registerCache.removeFragmentTempUsage(temp2);
        }
        else {
            code = 'tex ' + temp1 + ', ' + this._uvVarying + ', ' + inputTexture + ' <2d,linear,clamp>\n' +
                'mul ' + temp1 + ', ' + temp1 + ', ' + mask + '\n' +
                'dp4 ' + temp1 + ', ' + temp1 + ', ' + decode + '\n' +
                // eslint-disable-next-line max-len
                this._op + temp1 + ', ' + (this._th ? threshold : temp1) + ', ' + (this._th ? temp1 : threshold) + '\n' +
                'mul ' + temp1 + ', ' + temp1 + ', ' + color + '\n' +
                'mov oc, ' + temp1 + '\n';
        }
        this._registerCache.removeFragmentTempUsage(temp1);
        return code;
    };
    ThresholdTask.prototype.activate = function (stage, projection, depthTexture) {
        _super.prototype.computeVertexData.call(this);
        var tVector = new Vector3D(this._thresholdRGBA.x * this._maskRGBA.x, this._thresholdRGBA.y * this._maskRGBA.y, this._thresholdRGBA.z * this._maskRGBA.z, this._thresholdRGBA.w * this._maskRGBA.w);
        var tValue = tVector.dotProduct(this._decodeVector);
        var index = this._fragmentConstantsIndex;
        var data = this._fragmentConstantData;
        data[index] = tValue;
        data[index + 1] = tValue;
        data[index + 2] = tValue;
        data[index + 3] = tValue;
        data[index + 4] = this._colorRGBA.x;
        data[index + 5] = this._colorRGBA.y;
        data[index + 6] = this._colorRGBA.z;
        data[index + 7] = this._colorRGBA.w;
        data[index + 8] = this._maskRGBA.x;
        data[index + 9] = this._maskRGBA.y;
        data[index + 10] = this._maskRGBA.z;
        data[index + 11] = this._maskRGBA.w;
        var context = stage.context;
        context.setProgramConstantsFromArray(ContextGLProgramType.VERTEX, this._vertexConstantData);
        context.setProgramConstantsFromArray(ContextGLProgramType.FRAGMENT, this._fragmentConstantData);
        this._source.getAbstraction(stage).activate(this.sourceSamplerIndex);
    };
    return ThresholdTask;
}(TaskBase));
export { ThresholdTask };
